/**
 * Magic Link Authentication Action Hooks
 *
 * These hooks provide high-level magic link authentication actions
 * that convert Stytch responses to our domain types.
 */
import { discoveryOrganizationTimestamps } from './discovery-organization-timestamps';
import { useCallback } from 'react';
import { useStytchWrappers } from './stytch-hooks';
/**
 * Hook to send magic link email for discovery flow
 */
export function useSendMagicLink() {
    const { sendDiscoveryEmail } = useStytchWrappers();
    return useCallback(async (email, options) => {
        const result = await sendDiscoveryEmail(email, options);
        if (result.success) {
            return {
                success: true,
                emailId: typeof result.data === 'object' &&
                    result.data !== null &&
                    'email_id' in result.data &&
                    typeof result.data.email_id === 'string'
                    ? result.data.email_id
                    : undefined,
                data: result.data,
            };
        }
        else {
            return {
                success: false,
                error: result.error,
                data: result.data,
            };
        }
    }, [sendDiscoveryEmail]);
}
/**
 * Hook to authenticate magic link token
 */
export function useAuthenticateDiscoveryMagicLink() {
    const { authenticateDiscovery } = useStytchWrappers();
    return useCallback(async (token) => {
        // Use discovery flow for all magic link authentication
        const result = await authenticateDiscovery(token);
        if (result.success) {
            // Check if the user has discovered organizations
            const hasExistingOrganization = result.discovered_organizations && result.discovered_organizations.length > 0;
            return {
                success: true,
                intermediateSessionToken: result.intermediate_session_token,
                emailAddress: result.email_address,
                discoveredOrganizations: result.discovered_organizations?.map(org => ({
                    id: org.organization.organization_id,
                    name: org.organization.organization_name,
                    domain: org.organization.organization_slug || '',
                    member_count: 0, // Would need to fetch this separately
                    ...discoveryOrganizationTimestamps(org.organization),
                    stytch_organization_id: org.organization.organization_id,
                    membershipType: org.membership?.type,
                })),
                hasExistingOrganization,
                data: result.data,
            };
        }
        else {
            return {
                success: false,
                error: result.error,
                data: result.data,
            };
        }
    }, [authenticateDiscovery]);
}
/**
 * Hook to authenticate magic link token (legacy flow)
 */
export function useAuthenticateMagicLink() {
    const { authenticateMagicLink } = useStytchWrappers();
    return useCallback(async (token) => {
        const result = await authenticateMagicLink(token);
        if (result.success) {
            return {
                success: true,
                member: result.member,
                memberSession: result.memberSession,
                sessionToken: result.sessionToken,
                sessionJwt: result.sessionJwt,
                organization: result.organization,
                intermediateSessionToken: result.data.intermediate_session_token,
                memberId: result.memberId,
                data: result.data,
            };
        }
        else {
            return {
                success: false,
                error: result.error,
                data: result.data,
            };
        }
    }, [authenticateMagicLink]);
}
